import { getQueryString, updateUrlParameter, openWindow } from '@umu/umu-dom';
import { isDingding } from '@umu/umu-ua';

type TMiniAppEnv = 'dd' | 'wx';

const GROUP_URL_REGEX = /course\/?\?groupId=(\d+)/;
const MP_COURSE_HOME = '/pages/courseHome/index';
const MP_STATIC_WEBVIEW = '/pages/staticWebview/index';
/**
 * 当前页面启动来源是否为课程小程序
 */
export function isFromCourseMiniApp(): false | TMiniAppEnv {
    const isCourseMiniApp = (window as any).pageData?.config?.is_course_mini_app;
    if (!isCourseMiniApp) {
        return false;
    }
    return isDingding ? 'dd' : 'wx';
}

export function isFromMideaMiniApp(): boolean {
    return !!(window as any).pageData?.config?.is_in_midea_mini_app;
}
interface IMiniAppApiOptions {
    success: (res?: any) => void;
    fail: (res?: any) => void;
    complete: (res?: any) => void;
}
export function addMiniAppOriginToUrl(url: string) {
    if (isFromCourseMiniApp()) {
        const isFromMiniCourse = getQueryString('fromMiniCourseOrigin');
        if (isFromMiniCourse) {
            // 传递参数
            return updateUrlParameter(url, 'fromMiniCourseOrigin', isFromMiniCourse);
        }
    }
    return url;
}
/**
 * 统一钉钉和微信小程序的api
 * 需要先判断当前页面启动来源是否为课程小程序
 */
export function getMiniAppSdk() {
    const env = isFromCourseMiniApp();
    const sdkMap = {
        dd: (window as any).dd as any,
        wx: (window as any).wx?.miniProgram as any,
    };

    const envSdk = sdkMap[env as TMiniAppEnv];
    const navigateTo = (url: string, options?: IMiniAppApiOptions) => {
        return envSdk.navigateTo({ url, ...(options || {}) });
    };
    const postMessage = (data: any) => {
        return envSdk.postMessage({ data });
    };
    const navigateBack = (delta?: number) => {
        return envSdk.navigateBack(delta);
    };
    const redirectTo = (url: string, options?: IMiniAppApiOptions) => {
        return envSdk.redirectTo({ url, ...(options || {}) });
    };
    return {
        navigateTo,
        navigateBack,
        postMessage,
        redirectTo,
    };
}
export const isGroupUrl = (href: string) => {
    const match = href.match(GROUP_URL_REGEX);
    return match ? match[1] : false;
};

/**
 * 检查给定的 URL 是否为移动端主页面路径（pathname === '/main'）
 */
export const isMobileMainUrl = (url: string): boolean => {
    if (!url) {
        return false;
    }
    try {
        return new URL(url).pathname === '/main';
    } catch {
        return false;
    }
};
/**
 * 从m端页面打开课程，处理在课程小程序中打开的特殊场景
 * https://app.asana.com/0/1201311184760466/1208099431983400/f
 */
export function openCourseInMp(
    params: { groupId?: number | string; url?: string; options?: any; target?: '_blank' | '_self' },
    customAction?: () => void,
) {
    const groupId = params.groupId || isGroupUrl(params.url || '');
    if (isFromCourseMiniApp() && groupId) {
        getMiniAppSdk().navigateTo(`${MP_COURSE_HOME}?groupId=${groupId}`);
        return;
    }
    if (isFromMideaMiniApp() && params.url) {
        openUrlInMp(params.url);
        return;
    }
    if (customAction) {
        customAction();
    } else {
        params.url && openWindow(params.url, params.target);
    }
}

export const getCurrentUmuId = (): string => {
    const data = (window as any).pageData?.data;
    return String(
        data?.user_info?.umu_id ||
            data?.userInfo?.umu_id ||
            data?.teacher_info?.umu_id ||
            data?.teacherInfo?.umu_id ||
            '0',
    );
};

export async function openUrlInMp(url: string) {
    // 判断是否是移动端主页面跳转移动端主页面
    const isMobileMainToMobileMain = isMobileMainUrl(window.location.href) && isMobileMainUrl(url);
    if (isFromMideaMiniApp() && !isMobileMainToMobileMain) {
        const fullUrl = /^https?:\/\//.test(url) ? url : `${window.location.origin}${url}`;
        const groupId = isGroupUrl(fullUrl);
        if (groupId) {
            getMiniAppSdk().navigateTo(`${MP_COURSE_HOME}?groupId=${groupId}`);
        } else {
            getMiniAppSdk().navigateTo(`${MP_STATIC_WEBVIEW}?url=${encodeURIComponent(fullUrl)}`);
        }
    } else {
        window.location.href = url;
    }
}

const getLogoSize = (aspectRatio: number) => {
    const maxWidth = 80;
    const maxHeight = 38;
    // 计算图片显示的宽高
    let imgWidth = maxWidth;
    let imgHeight = maxWidth / aspectRatio;
    if (imgHeight > maxHeight) {
        imgHeight = maxHeight;
        imgWidth = maxHeight * aspectRatio;
    }
    return { width: `${Math.floor(imgWidth)}px`, height: `${Math.floor(imgHeight)}px` };
};

export const getLogoStyle = (url: string, callback: (style: { width: string; height: string }) => void) => {
    if (!url) return;
    const img = new Image();
    const cleanup = () => {
        img.onload = null;
        img.onerror = null;
    };
    img.onload = () => {
        if (img.width && img.height) {
            const aspectRatio = img.width / img.height;
            callback(getLogoSize(aspectRatio));
        }
        cleanup();
    };
    img.onerror = cleanup;
    img.src = url;
};
